import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { CurrencyInput } from "../../components/CurrencyInput";
import { VenueAddressFields } from "../../components/VenueAddressFields";
import { DirectoryDrawer } from "../../components/directory/DirectoryDrawer";
import { DirectoryAvatar } from "../../components/directory/DirectoryAvatar";
import { PhotoUploadField } from "../../components/directory/PhotoUploadField";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { venuesApi } from "../../api";
import { QueryErrorBanner, ListSkeletonCards } from "../../components/feedback/QueryFeedback";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { Venue } from "../../types";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { validateAddressForm } from "../../utils/addressValidation";
import {
  buildVenuePayload,
  emptyVenueForm,
  formatVenueDisplayAddress,
  venueToForm,
  type VenueFormState,
} from "../../utils/venueAddress";
import { formatINR } from "../../utils/format";
import "../../components/directory/directoryList.css";
import "./VenuesPage.css";

type Props = {
  workspace: WorkspaceId;
};

const VenuesPage: React.FC<Props> = ({ workspace }) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<VenueFormState>(emptyVenueForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Venue | null>(null);
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string | null>(null);

  const patchForm = (patch: Partial<VenueFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const venuesQuery = useQuery({
    queryKey: token ? queryKeys.venues.list(workspace) : queryKeys.venues.all(workspace),
    queryFn: () => venuesApi.list(token!),
    enabled: Boolean(token),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.venues.list(workspace) });
  };

  const createMutation = useMutation({
    mutationFn: async (payload: VenueFormState) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return venuesApi.create(buildVenuePayload(payload), token);
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; formData: VenueFormState }) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return venuesApi.update(payload.id, buildVenuePayload(payload.formData), token);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return venuesApi.remove(id, token);
    },
    onSuccess: invalidate,
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!token || !editingId) throw new Error("Save the venue before uploading a photo.");
      return venuesApi.uploadPhoto(editingId, file, token);
    },
    onSuccess: async (updated) => {
      setUploadedPhotoUrl(updated.photo_url ?? null);
      await invalidate();
    },
  });

  const rows = useMemo<Venue[]>(() => venuesQuery.data ?? [], [venuesQuery.data]);
  const loading = venuesQuery.isPending;
  const errorMsg =
    venuesQuery.error instanceof Error ? venuesQuery.error.message : venuesQuery.error ? String(venuesQuery.error) : "";

  const resetForm = () => {
    setForm(emptyVenueForm);
    setEditingId(null);
    setFormOpen(false);
    setUploadedPhotoUrl(null);
  };

  const openCreate = () => {
    setForm(emptyVenueForm);
    setEditingId(null);
    setFormOpen(true);
    setUploadedPhotoUrl(null);
  };

  const beginEdit = (venue: Venue) => {
    setEditingId(venue.id);
    setForm(venueToForm(venue));
    setFormOpen(true);
    setUploadedPhotoUrl(venue.photo_url ?? null);
  };

  const editingVenue = editingId ? rows.find((venue) => venue.id === editingId) ?? null : null;
  const currentPhotoUrl = uploadedPhotoUrl ?? editingVenue?.photo_url ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const addressCheck = validateAddressForm(form);
    if (!addressCheck.ok) {
      toast.error(addressCheck.message);
      return;
    }
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, formData: form });
        toast.success("Venue updated");
      } else {
        await createMutation.mutateAsync(form);
        toast.success("Venue created");
      }
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : editingId ? "Failed to update venue" : "Failed to create venue");
    }
  };

  const handleDelete = (venue: Venue) => {
    setPendingDelete(venue);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      if (editingId === pendingDelete.id) resetForm();
      toast.success("Venue deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete venue");
    } finally {
      setPendingDelete(null);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="app-shell-dash venues-page">
      <div className="app-shell-dash-toolbar">
        <div>
          <h1 className="app-shell-page-title venues-page__title">Venues</h1>
          <p className="app-shell-lede venues-page__lede">
            Venue profiles with full postal addresses for onboarding and logistics.
          </p>
        </div>
        <button type="button" className="eventos-primary-btn" onClick={openCreate}>
          Add venue
        </button>
      </div>

      {venuesQuery.isError ? (
        <QueryErrorBanner
          message={errorMsg || "Unable to load venues"}
          onRetry={() => void venuesQuery.refetch()}
          retrying={venuesQuery.isFetching}
        />
      ) : null}

      <div className="venues-page__layout directory-page__list-only">
        <div className="directory-page__list-bar">
          <span className="directory-page__count">
            {loading ? "Loading…" : `${rows.length} venue${rows.length === 1 ? "" : "s"}`}
          </span>
          <button type="button" className="eventos-nav-btn" onClick={() => void venuesQuery.refetch()} disabled={venuesQuery.isFetching}>
            {venuesQuery.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <div className="directory-page__list">
          {loading ? (
            <ListSkeletonCards rows={6} />
          ) : rows.length === 0 ? (
            <p className="directory-page__empty">No venues yet. Add your first venue to get started.</p>
          ) : (
            rows.map((venue) => {
              const address = formatVenueDisplayAddress(venue);
              return (
                <article
                  key={venue.id}
                  className={`directory-page__row${editingId === venue.id ? " is-editing" : ""}`}
                >
                  <DirectoryAvatar name={venue.venue_name} photoUrl={venue.photo_url} className="directory-page__avatar" />
                  <div className="directory-page__row-main">
                    <div className="directory-page__row-head">
                      <span className="directory-page__row-name">{venue.venue_name}</span>
                      {venue.capacity ? (
                        <span className="directory-page__pill">{venue.capacity.toLocaleString("en-IN")} cap</span>
                      ) : null}
                    </div>
                    <div className="directory-page__row-details">
                      <div className="directory-page__detail">
                        <span className="directory-page__detail-label">Address</span>
                        <span className="directory-page__detail-value directory-page__detail-value--muted" title={address}>
                          {address}
                        </span>
                      </div>
                      <div className="directory-page__detail">
                        <span className="directory-page__detail-label">Capacity</span>
                        <span className="directory-page__detail-value">
                          {venue.capacity?.toLocaleString("en-IN") ?? "—"}
                        </span>
                      </div>
                      <div className="directory-page__detail">
                        <span className="directory-page__detail-label">Base cost</span>
                        <span className="directory-page__detail-value">
                          {venue.base_cost != null ? formatINR(venue.base_cost) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="directory-page__row-actions">
                    <button type="button" className="eventos-nav-btn" onClick={() => beginEdit(venue)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="eventos-nav-btn"
                      onClick={() => handleDelete(venue)}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>

      <DirectoryDrawer
        open={formOpen}
        size="form"
        placement="center"
        title={editingId ? "Edit venue" : "Add venue"}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="eventos-form directory-page__drawer-form">
          <div className="app-form-section" role="group" aria-labelledby="venue-sec-details">
            <div id="venue-sec-details" className="app-form-section-title">
              Venue details
            </div>
            {editingId ? (
              <PhotoUploadField
                name={form.venue_name || "Venue"}
                photoUrl={currentPhotoUrl}
                uploading={uploadPhotoMutation.isPending}
                onUpload={(file) => uploadPhotoMutation.mutateAsync(file)}
              />
            ) : null}
            <div className="eventos-form-grid">
              <label className="eventos-field">
                <span className="eventos-field-label">Venue name</span>
                <input
                  className="eventos-input"
                  value={form.venue_name}
                  onChange={(e) => patchForm({ venue_name: e.target.value })}
                  placeholder="NSCI Dome"
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Capacity</span>
                <input
                  className="eventos-input"
                  type="number"
                  min={0}
                  value={form.capacity}
                  onChange={(e) => patchForm({ capacity: e.target.value })}
                  placeholder="Maximum capacity"
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Base cost</span>
                <CurrencyInput
                  value={form.base_cost}
                  onChange={(e) => patchForm({ base_cost: e.target.value })}
                  placeholder="Typical booking cost"
                />
              </label>
            </div>
          </div>

          <div className="app-form-section" role="group" aria-labelledby="venue-sec-address">
            <div id="venue-sec-address" className="app-form-section-title">
              Full address
            </div>
            <p className="app-form-section-lede">Street address, city, district, state, and PIN code.</p>
            <VenueAddressFields
              form={form}
              onChange={patchForm}
              idPrefix={editingId ?? "new-venue"}
              venues={rows}
              districtOrphanValue={editingId ? form.district : null}
            />
          </div>

          <div className="app-form-actions">
            <button className="eventos-primary-btn" type="submit" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Save changes" : "Create venue"}
            </button>
            <button type="button" className="eventos-nav-btn" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </DirectoryDrawer>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete venue?"
        message={
          pendingDelete
            ? `"${pendingDelete.venue_name}" will be removed from your directory. Any events or schedules referencing this venue will no longer show it (those links are cleared, not deleted).`
            : ""
        }
        confirmLabel="Delete"
        danger
        loading={deleteMutation.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default VenuesPage;
